import {
  projectMembershipCounterRuntimeEvidence,
  type MembershipCounterEvidenceStatus,
} from './MembershipCounterRuntimeEvidenceProjectionEngine';

export type MembershipCounterCompletionDecision =
  | 'COMPLETE'
  | 'BLOCKED'
  | 'UNVERIFIED';

export interface MembershipCounterCompletionCheck {
  id:
    | 'live-counter-authority'
    | 'runtime-evidence-projection'
    | 'owner-seat-accounting'
    | 'sponsored-seat-counters'
    | 'invitation-counters'
    | 'family-profile-counters'
    | 'browser-derived-path-removed'
    | 'fail-closed-behavior'
    | 'single-dashboard-surface'
    | 'relocation-plan-locked';
  title: string;
  required: true;
  status: MembershipCounterEvidenceStatus;
  explanation: string;
}

export interface MembershipCounterCompletionGate {
  title: string;
  subsystem: 'membership';
  authorityDomain: 'membership-dashboard-counters';
  decision: MembershipCounterCompletionDecision;
  status: MembershipCounterEvidenceStatus;
  completionAllowed: boolean;

  requiredCheckCount: number;
  passedCheckCount: number;
  failedCheckCount: number;
  unverifiedCheckCount: number;

  failedChecks: MembershipCounterCompletionCheck[];
  unverifiedChecks: MembershipCounterCompletionCheck[];
  checks: MembershipCounterCompletionCheck[];

  dashboardRelocationAuthorized: boolean;
  destination:
    'Settings → Membership & Family Access';
  treeInviteShortcutRequired: true;

  constitutionalRule: string;
  nextMilestone: string;
  evaluatedAt: string;
}

export function evaluateMembershipCounterCompletionGate():
  MembershipCounterCompletionGate {
  const evidence =
    projectMembershipCounterRuntimeEvidence();

  const checks: MembershipCounterCompletionCheck[] = [
    {
      id: 'live-counter-authority',
      title: 'Live membership counter authority',
      required: true,
      status:
        evidence.status === 'PASS' &&
        evidence.rpc ===
          'get_membership_dashboard_counters'
          ? 'PASS'
          : evidence.status,
      explanation:
        'Membership counters must originate from the authenticated database RPC.',
    },
    {
      id: 'runtime-evidence-projection',
      title: 'Membership Governor counter evidence',
      required: true,
      status: evidence.status,
      explanation:
        'The certified counter authority must be visible in the Command Center.',
    },
    {
      id: 'owner-seat-accounting',
      title: 'Owner seat included in plan capacity',
      required: true,
      status:
        evidence.ownerSeatIncluded &&
        evidence.planCapacities.length === 4
          ? 'PASS'
          : 'FAIL',
      explanation:
        'All plan capacities must include the membership owner.',
    },
    {
      id: 'sponsored-seat-counters',
      title: 'Sponsored-seat counters',
      required: true,
      status:
        evidence.activeSponsoredCounter &&
        evidence.reservedSponsoredCounter &&
        evidence.occupiedSeatCounter &&
        evidence.availableSponsoredCounter &&
        evidence.returnedSponsoredCounter
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Active, reserved, occupied, available, and returned seat counters must be present.',
    },
    {
      id: 'invitation-counters',
      title: 'Invitation lifecycle counters',
      required: true,
      status:
        evidence.totalInviteCounter &&
        evidence.familyInviteCounter &&
        evidence.pendingInviteCounter &&
        evidence.acceptedInviteCounter &&
        evidence.expiredInviteCounter
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Total, family, pending, accepted, and expired invitation counters must be present.',
    },
    {
      id: 'family-profile-counters',
      title: 'Claimed and unclaimed family-profile counters',
      required: true,
      status:
        evidence.claimedProfileCounter &&
        evidence.unclaimedProfileCounter
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Claimed and unclaimed family-profile counters must be available.',
    },
    {
      id: 'browser-derived-path-removed',
      title: 'Browser-derived counter path removed',
      required: true,
      status:
        evidence.browserDerivedCounterPathRemoved
          ? 'PASS'
          : 'FAIL',
      explanation:
        'The browser must not recalculate authoritative counters from raw invite rows.',
    },
    {
      id: 'fail-closed-behavior',
      title: 'Counter failures fail closed',
      required: true,
      status:
        evidence.failClosedOnAuthorityError
          ? 'PASS'
          : 'FAIL',
      explanation:
        'The UI must not invent or preserve stale counts when authority loading fails.',
    },
    {
      id: 'single-dashboard-surface',
      title: 'Single dashboard surface',
      required: true,
      status:
        evidence.existingDashboardReused &&
        evidence.duplicateDashboardPrevented
          ? 'PASS'
          : 'FAIL',
      explanation:
        'The existing Membership Operations Center must remain the only full dashboard surface.',
    },
    {
      id: 'relocation-plan-locked',
      title: 'Settings relocation plan locked',
      required: true,
      status:
        evidence.plannedDashboardDestination ===
          'Settings → Membership & Family Access' &&
        evidence.treePageRetainsInviteShortcut &&
        evidence.relocationScheduledAfterCounterAuthority
          ? 'PASS'
          : 'FAIL',
      explanation:
        'The full dashboard must move to Settings while the tree retains a lightweight invite shortcut.',
    },
  ];

  const passedCheckCount = checks.filter(
    check => check.status === 'PASS',
  ).length;

  const failedChecks = checks.filter(
    check => check.status === 'FAIL',
  );

  const unverifiedChecks = checks.filter(
    check => check.status === 'UNVERIFIED',
  );

  const completionAllowed =
    passedCheckCount === checks.length &&
    failedChecks.length === 0 &&
    unverifiedChecks.length === 0;

  const decision: MembershipCounterCompletionDecision =
    completionAllowed
      ? 'COMPLETE'
      : failedChecks.length > 0
        ? 'BLOCKED'
        : 'UNVERIFIED';

  const status: MembershipCounterEvidenceStatus =
    decision === 'COMPLETE'
      ? 'PASS'
      : decision === 'BLOCKED'
        ? 'FAIL'
        : 'UNVERIFIED';

  return Object.freeze({
    title: 'Membership Counter Completion Gate',
    subsystem: 'membership',
    authorityDomain:
      'membership-dashboard-counters',
    decision,
    status,
    completionAllowed,

    requiredCheckCount: checks.length,
    passedCheckCount,
    failedCheckCount: failedChecks.length,
    unverifiedCheckCount:
      unverifiedChecks.length,

    failedChecks,
    unverifiedChecks,
    checks,

    dashboardRelocationAuthorized:
      completionAllowed,
    destination:
      'Settings → Membership & Family Access',
    treeInviteShortcutRequired: true,

    constitutionalRule:
      'Family identity is permanent. Capabilities are licensed. Resources are governed.',
    nextMilestone:
      'MAE-004D — Membership Dashboard Settings Relocation',
    evaluatedAt: new Date().toISOString(),
  });
}

export const MEMBERSHIP_COUNTER_COMPLETION_GATE_READY =
  true;
